import { z } from 'zod';

export const TIMESHEET_STATUSES = ['draft', 'submitted', 'approved', 'rejected'] as const;
export const timesheetStatusEnum = z.enum(TIMESHEET_STATUSES);
export type TimesheetStatus = z.infer<typeof timesheetStatusEnum>;

export const TIMESHEET_ENTRY_CATEGORIES = [
  'development',
  'design',
  'testing',
  'meeting',
  'review',
  'research',
  'other',
] as const;
export const timesheetEntryCategoryEnum = z.enum(TIMESHEET_ENTRY_CATEGORIES);
export type TimesheetEntryCategory = z.infer<typeof timesheetEntryCategoryEnum>;

/**
 * A weekly timesheet — deliberately independent of the Project/Sprint/Item
 * tracker (an entry's `projectId` is an optional soft link, not a hard
 * dependency): this is an HR-adjacent, self-service-then-approved workflow,
 * closer in shape to Leave requests than to Project Management's
 * membership model. Ported from Office_project's `Timesheet` model.
 */
const timesheetEntrySchema = z.object({
  date: z.coerce.date(),
  projectId: z.string().optional(),
  projectName: z.string().trim().max(160).optional().or(z.literal('')),
  taskDescription: z.string().trim().min(1, 'Task description is required').max(500),
  hours: z.coerce
    .number()
    .min(0.5, 'Hours must be at least 0.5')
    .max(24, 'Hours cannot exceed 24 in a single entry'),
  category: timesheetEntryCategoryEnum.default('development'),
});

export type TimesheetEntryInput = z.infer<typeof timesheetEntrySchema>;

export const weekQuerySchema = z.object({
  weekStart: z.coerce.date(),
});

export type WeekQuery = z.infer<typeof weekQuerySchema>;

export const updateTimesheetEntriesSchema = z.object({
  entries: z.array(timesheetEntrySchema).default([]),
});

export type UpdateTimesheetEntriesInput = z.infer<typeof updateTimesheetEntriesSchema>;

export const reviewTimesheetSchema = z.object({
  action: z.enum(['approved', 'rejected']),
  reviewNote: z.string().trim().max(1000).optional().or(z.literal('')),
});

export type ReviewTimesheetInput = z.infer<typeof reviewTimesheetSchema>;

export const timesheetListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  status: timesheetStatusEnum.optional(),
  employeeId: z.string().optional(),
});

export type TimesheetListQuery = z.infer<typeof timesheetListQuerySchema>;
